import { useEffect, useRef } from "react";
import { EditorState } from "prosemirror-state";
import { EditorView } from "prosemirror-view";
import { buildInitialDoc, buildPlugins } from "./setup";

export type Canvas = { width: number; height: number; padding: number };

export function Editor({
  canvas,
  onReady,
  onState,
}: {
  canvas: Canvas;
  onReady: (view: EditorView) => void;
  onState: (state: EditorState) => void;
}) {
  const hostRef = useRef<HTMLDivElement>(null);
  const viewRef = useRef<EditorView | null>(null);

  const onReadyRef = useRef(onReady);
  const onStateRef = useRef(onState);
  onReadyRef.current = onReady;
  onStateRef.current = onState;

  useEffect(() => {
    const host = hostRef.current;
    if (!host) return;
    const state = EditorState.create({
      doc: buildInitialDoc(),
      plugins: buildPlugins(),
    });
    const view = new EditorView(host, {
      state,
      attributes: labelAttrs(canvas),
      dispatchTransaction: (tr) => {
        const next = view.state.apply(tr);
        view.updateState(next);
        onStateRef.current(next);
      },
    });
    viewRef.current = view;
    onReadyRef.current(view);
    onStateRef.current(state);
    return () => {
      view.destroy();
      viewRef.current = null;
    };
    // mount once; canvas size updates are handled in the next effect
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const view = viewRef.current;
    if (!view) return;
    view.setProps({ attributes: labelAttrs(canvas) });
  }, [canvas.width, canvas.height, canvas.padding]);

  return <div ref={hostRef} />;
}

function labelAttrs(c: Canvas) {
  return {
    class: "label-editor",
    "data-label-root": "",
    style: `width:${c.width}px;height:${c.height}px;padding:${c.padding}px`,
  };
}
